<script lang="ts">
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { X } from '@lucide/vue';

export default {
  name: 'UploadPolicyDocumentModal',
  components: {
    AppModal,
    AppSelect,
    X,
  },
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { UploadDocumentRequest, DocumentType, UploadDocumentResponse } from '@/types/v2/Document';

type UploadModalFile = Partial<UploadDocumentRequest> & {
  filename: string;
  data: File;
  validationErrors: string[];
};

const props = defineProps<{
  title: string;
  description: string;
  isUserAllowed: boolean;
  validator?: (fileData: string) => string[];
  upload: (fileData: Partial<UploadDocumentRequest>[]) => Promise<UploadDocumentResponse>;
  acceptedContentTypes: string;
  policyDocumentTypes: DocumentType[];
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

// refs
// -----
const fileInput = ref<HTMLInputElement | null>(null);
const errors = ref<string[]>([]);
const files = ref<UploadModalFile[]>([]);
const selectedDocumentTypes = ref<DocumentType[]>([]);
const selectedDocumentType = ref<number | undefined>(undefined);
const isValidating = ref(false);
const isUploading = ref(false);
const isValid = ref(false);
const isUploaded = ref(false);
const loading = ref(false);

// computed
// ---------
const policyTypeDisabled = computed(() => {
  return props.policyDocumentTypes && props.policyDocumentTypes.length === 1;
});

const canUpload = computed(() => {
  return !loading.value && !isUploading.value && isValid.value;
});

// methods
// --------
async function handleUploadDocument(): Promise<boolean | undefined> {
  if (files.value.length === 0 || !isValid.value) {
    return false;
  }
  isUploading.value = true;
  const fileToUpload = files.value.map(
    (f: UploadModalFile, i: number): Partial<UploadDocumentRequest> => ({
      fileName: f.filename,
      document: f.data,
      type: selectedDocumentTypes.value[i].name,
      domain: 'Policy',
    }),
  );

  loading.value = true;
  const { success, messages } = await props.upload(fileToUpload);
  loading.value = false;

  errors.value = messages;
  isUploading.value = false;
  isUploaded.value = success;
  if (!success) {
    return false;
  }
}

async function readFile(event: Event) {
  if (!event || !event.target) {
    return;
  }
  const filesToRead = (event.target as HTMLInputElement).files;
  if (!filesToRead || filesToRead.length === 0) {
    return;
  }
  isValid.value = false;
  isUploaded.value = false;
  errors.value = [];
  isValidating.value = true;
  files.value = await Promise.all(
    Array.from(filesToRead).map((file) => {
      const reader = new FileReader();
      return new Promise<UploadModalFile>((resolve) => {
        reader.onload = () => {
          const data = reader.result;
          let asString: string;
          if (typeof data === 'string') {
            asString = data;
          } else if (data instanceof ArrayBuffer) {
            try {
              asString = String.fromCharCode(...new Uint8Array(data));
            } catch (e) {
              // file too big for string
              asString = '';
            }
          } else {
            asString = '';
          }
          resolve({
            filename: file.name,
            data: file,
            type: undefined,
            validationErrors: props.validator ? props.validator(asString) ?? [] : [],
          });
        };
        reader.readAsArrayBuffer(file);
      });
    }),
  );
  isValidating.value = false;
  isValid.value = !files.value.some((x) => x.validationErrors.length > 0);
}

function resetFile() {
  if (!isValidating.value && !isUploading.value) {
    files.value = [];
    isValid.value = false;
    isUploaded.value = false;
    errors.value = [];
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
}

// lifecycles
// -----------
onMounted(() => {
  if (props.policyDocumentTypes && props.policyDocumentTypes.length > 0) {
    selectedDocumentType.value = props.policyDocumentTypes[0].id;
    selectedDocumentTypes.value = [props.policyDocumentTypes[0]];
  }
});
</script>

<template>
  <AppModal
    :show="true"
    :loading="loading"
    action-button-label="Upload"
    modal-class="max-w-md"
    :action-disabled="!canUpload"
    :handle-cancel="() => emit('close')"
    :handle-action="handleUploadDocument"
  >
    <template v-slot:title>
      <h2 class="font-normal text-primary">{{ title }}</h2>
      <p class="italic">{{ description }}</p>
    </template>
    <template v-slot:content>
      <div class="flex flex-col items-center gap-4">
        <div class="w-full">
          <label class="label">
            <span class="label-text text-xs text-gray-500">Document Type</span>
          </label>

          <AppSelect
            v-model="selectedDocumentType"
            :options="policyDocumentTypes.map(type => ({
              label: type.name,
              value: type.id
            }))"
            class="w-full"
            :disabled="policyTypeDisabled"
          />
        </div>

        <div class="relative flex w-full flex-row">
          <input
            class="file-input file-input-bordered file-input-sm w-full border border-solid"
            ref="fileInput"
            type="file"
            @change="readFile"
            :accept="acceptedContentTypes"
          />

          <X
            v-if="files && files.length > 0"
            @click="resetFile"
            class="absolute right-1 top-1 cursor-pointer text-gray-300 hover:text-gray-500"
          />
        </div>

        <div class="upload-errors" v-if="errors.length > 0 && !isValidating && !isUploaded">
          <span class="text-error">Upload failed due to the following:</span>
          <ul>
            <li v-for="(error, index) of errors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
    </template>
  </AppModal>
</template>
